import { combineReducers } from 'redux'
import { loadingActions } from 'store/loader'
import request, { generateNotyMessage } from 'utils/request'
import { actions as notyActions } from 'layouts/ErrorBox'
import STRINGS from 'utils/strings'
import { redirectVisit, onRedirectVisit } from '../utils/visitRedirection'
import { getCreateVisitTemplateErrors } from '../utils/visitTemplateValidation'
import {
  actions as visitScheduleActions,
  setScheduleVisitTemplates,
} from '../../../../VisitSchedulesPage/modules/VisitSchedules'
import { getStudyVersionID } from '../../../../../../../../../../../utils/studyConfigUtils'

//
// Actions
//
const INIT_VISIT_TEMPLATE = 'INIT_VISIT_TEMPLATE'
export const UPDATE_VISIT_TEMPLATE_NAME = 'UPDATE_VISIT_TEMPLATE_NAME'
const UPDATE_ORIGINAL_VISIT_TEMPLATE = 'UPDATE_ORIGINAL_VISIT_TEMPLATE'
export const SET_VISIT_TEMPLATE = 'SET_VISIT_TEMPLATE'

// ERRORS
const ADD_VISIT_TEMPLATE_ERRORS = 'ADD_VISIT_TEMPLATE_ERRORS'
const CLEAR_VISIT_TEMPLATE_ERRORS = 'CLEAR_VISIT_TEMPLATE_ERRORS'
//
// Action Creators
//

const initPage = () => dispatch => {
  return dispatch({
    type: INIT_VISIT_TEMPLATE,
  })
}

const setScheduleVisitTemplate = ({ study_visit_schedule }, visitTemplateId, templateVisitId) => {
  return {
    type: SET_VISIT_TEMPLATE,
    visitTemplate: returnSpecificVisitTemplate(study_visit_schedule, visitTemplateId),
    templateVisitId,
  }
}

const setScheduleVisitTemplate2 = ({ study_visit_schedule }, { armScheduleId, visitId, phaseId, cycleId }) => {
  return {
    type: SET_VISIT_TEMPLATE,
    visitTemplate: returnSpecificVisitTemplate(study_visit_schedule, armScheduleId),
    templateVisitId: visitId,
    phaseId,
    cycleId,
  }
}

const updateVisitName = text => dispatch => dispatch({ type: UPDATE_VISIT_TEMPLATE_NAME, text })

const addVisitTemplateErrors = errors => dispatch => dispatch({ type: ADD_VISIT_TEMPLATE_ERRORS, errors })

const clearVisitTemplateErrors = () => dispatch => dispatch({ type: CLEAR_VISIT_TEMPLATE_ERRORS })
//
// Utils
//
const defaultVisitsTemplateState = {
  name: '',
  visits: [],
}

//
// API functions
//
const saveVisitTemplateRequest = ({ studyID, body, visitTemplateID }) => (dispatch, getState) => {
  const { study } = getState() || {}
  const isArmsSupported = study?.currentStudy?.config?.visit_support?.arms_enabled

  const url = `/control/studies/${studyID}/study_schedules${visitTemplateID ? `/${visitTemplateID}` : ''}`
  return dispatch(
    request({
      url,
      body,
      method: visitTemplateID ? 'PATCH' : 'POST',
      successMessage: isArmsSupported ? STRINGS.armSavedSuccess : STRINGS.visitScheduleSavedSuccess,
      hasLoader: true,
      success: json => json,
    }),
  )
}

const fetchVisitTemplate = (studyID, visitTemplateId, templateVisitId) => (dispatch, getState) => {
  const { study, location } = getState() || {}
  const hasStudyPromotion = !!study?.currentStudy?.config?.study_promotion
  const studyVersionID = getStudyVersionID(location)
  const getQueryParams = () => {
    if (hasStudyPromotion) {
      return `?study_version_id=${studyVersionID}`
    }
    return ''
  }
  const url = `/control/studies/${studyID}/study_schedules${getQueryParams()}`
  const success = payload => {
    const path = location.pathname.match(/([^/]+$)/)[0]
    const isVisitCycle = study?.currentStudy?.config?.visit_support?.version > 1
    if (isVisitCycle && path === 'edit') {
      const newVisitTemplateIDs = visitTemplateId.split('_')
      dispatch(
        setScheduleVisitTemplate2(payload, {
          armScheduleId: newVisitTemplateIDs[0],
          visitId: templateVisitId,
          phaseId: newVisitTemplateIDs[1],
          cycleId: newVisitTemplateIDs[2],
        }),
      )
    } else {
      dispatch(setScheduleVisitTemplate(payload, visitTemplateId, templateVisitId))
    }
    dispatch(setScheduleVisitTemplates(payload, isVisitCycle))
  }
  return dispatch(
    request({
      url,
      hasLoader: true,
      success,
    }),
  )
}

//
// Util Functions
//
const saveVisitTemplate = ({ studyID, visitTemplate, isUpdate, visitTemplateID }) => (dispatch, getState) => {
  const { study, location } = getState()
  const hasStudyPromotion = !!study?.currentStudy?.config?.study_promotion
  const studyVersionID = getStudyVersionID(location)

  const { name } = visitTemplate
  const defaultVisitTemplateJSON = {
    study_visit_schedule: {
      name,
      metadata: {
        blocking_window: false,
        disable_future_status_update: false,
      },
      visits: [],
    },
    study_version_id: hasStudyPromotion ? studyVersionID : undefined,
  }

  const success = resJSON => {
    dispatch({
      type: UPDATE_ORIGINAL_VISIT_TEMPLATE,
      json: visitTemplate,
    })
    return Promise.resolve(resJSON)
  }

  const body = JSON.stringify(
    isUpdate
      ? { study_visit_schedule: visitTemplate, study_version_id: hasStudyPromotion ? studyVersionID : undefined }
      : defaultVisitTemplateJSON,
  )

  return dispatch(saveVisitTemplateRequest({ studyID, body, visitTemplateID })).then(success)
}

const createVisitPhase = ({ studyID, visitScheduleId }) => (dispatch, getState) => {
  const { study, location } = getState()
  const hasStudyPromotion = !!study?.currentStudy?.config?.study_promotion
  const studyVersionID = getStudyVersionID(location)
  const success = payload => {
    dispatch(visitScheduleActions.fetchVisitSchedules(studyID))
  }

  const body = {
    phases: [
      {
        name: 'phase #1',
        metadata: {},
        qualifying_criteria: {},
        // TODO: to be updated once Phases are implementec
        length_value: 999,
        length_type: 'months',
      },
    ],
    study_version_id: hasStudyPromotion ? studyVersionID : undefined,
  }

  return dispatch(
    request({
      url: `/control/studies/${studyID}/study_schedules/${visitScheduleId}/phases`,
      method: 'POST',
      hasLoader: true,
      success,
      body: JSON.stringify(body),
    }),
  )
}

const onSaveVisitTemplate = ({ studyID, redirectPath, queryParams, isUpdate, visitTemplateID }) => (
  dispatch,
  getState,
) => {
  const { visitTemplateReducer, subroute } = getState()
  const { visitTemplate } = visitTemplateReducer
  const errors = getCreateVisitTemplateErrors(visitTemplate)
  const valid = !(Object.values(errors).filter(val => val).length >= 1)
  if (valid) {
    dispatch(clearVisitTemplateErrors())
    dispatch(saveVisitTemplate({ studyID, ...visitTemplateReducer, isUpdate, visitTemplateID })).then(resJSON => {
      if (resJSON?.study_visit_template_id) {
        dispatch(createVisitPhase({ studyID, visitScheduleId: resJSON?.study_visit_template_id }))
      }
      dispatch(onRedirectVisit({ resJSON, redirectPath, studyID, queryParams, visitScheduleSubroute: subroute }))
    })
  } else {
    dispatch(addVisitTemplateErrors(errors))
    dispatch(
      notyActions.showError({
        text: generateNotyMessage(STRINGS.pleaseCheckErr, false),
      }),
    )
  }
}

const onDeleteArm = ({ studyID, visitTemplateID }) => (dispatch, getState) => {
  const { study, location } = getState()
  const hasStudyPromotion = !!study?.currentStudy?.config?.study_promotion
  const studyVersionID = getStudyVersionID(location)

  const getQueryParams = () => {
    if (hasStudyPromotion) {
      return `?study_version_id=${studyVersionID}`
    }
    return ''
  }

  const url = `/control/studies/${studyID}/study_schedules/${visitTemplateID}${getQueryParams()}`
  const success = resJSON => {
    dispatch(onRedirectVisit({ resJSON, studyID }))
  }

  return dispatch(
    request({
      url,
      method: 'DELETE',
      successMessage: STRINGS.armDelSuccess,
      failMessage: STRINGS.armDelFail,
      success,
    }),
  )
}

const returnSpecificVisitTemplate = (visitSchedules, visitTemplateId) => {
  for (let i = 0; i < visitSchedules.length; i++) {
    const visitSchedule = visitSchedules[i]
    if (visitSchedule.id === visitTemplateId) return visitSchedule
  }
}

//
// Reducers
//

const visitTemplate = (state = defaultVisitsTemplateState, action) => {
  const newState = { ...state }
  switch (action.type) {
    case INIT_VISIT_TEMPLATE:
      return defaultVisitsTemplateState
    case UPDATE_VISIT_TEMPLATE_NAME:
      return { ...newState, name: action.text }
    case SET_VISIT_TEMPLATE: {
      return action.visitTemplate
    }
    default:
      return state
  }
}

const visitTemplateErrors = (state = {}, action) => {
  switch (action.type) {
    case INIT_VISIT_TEMPLATE:
    case CLEAR_VISIT_TEMPLATE_ERRORS:
      return {}
    case ADD_VISIT_TEMPLATE_ERRORS:
      return action.errors
    default:
      return state
  }
}

const originalVisitTemplate = (state = {}, action) => {
  if (action.type === INIT_VISIT_TEMPLATE || action.type === UPDATE_ORIGINAL_VISIT_TEMPLATE) {
    return JSON.parse(JSON.stringify(action.json || defaultVisitsTemplateState))
  }
  return state
}

export default combineReducers({ visitTemplate, visitTemplateErrors, originalVisitTemplate })

export const visitTemplateActions = {
  fetchVisitTemplate,
  initPage,
  onSaveVisitTemplate,
  onDeleteArm,
  updateVisitName,
}
